import {
  AwardExternalFragment,
  AwardExternalPointsFragment,
  AwardPointsFragment,
  AwardPointsPerFragment,
  AwardPointsPerInput,
  AwardVouchersFragment,
  EarningMechanismConfigurationInput,
  EarningMechanismExternalFulfillmentFragment,
  EarningMechanismLoyaltyEventFragment,
  EarningMechanismMembershipEventFragment,
  EarningMechanismPointSpendFragment,
  EarningMechanismProductDiscountFragment,
  EarningMechanismProductDiscountInput,
  EarningMechanismProductPurchaseFragment,
  EarningMechanismSpinWheelGameFragment,
  EarningMechanismTotalEligiblePurchaseFragment,
  EarningMechanismTotalEligiblePurchaseOverTimeFragment,
  EarningMechanismTotalSpendOnTransactionFragment,
  EarningMechanismTotalSpendOverTimeFragment,
  EarningMechanismTransactionDiscountFragment,
  EarningMechanismTransactionDiscountInput,
  EarningMechanismVarietyDiscountFragment,
  EarningMechanismVarietyDiscountInput,
  EarningMechanismVarietyPurchaseFragment,
  EarningMechanismVarietyPurchaseOverTimeFragment,
  EarningMechanismVisitsFragment,
  OfferFragment,
  Random_Award_Type,
} from '@occc/graphql';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { awardHelper } from '@occc-app/offerHelpers/utils/awardHelper';
import { awardInputCreator } from '@occc-app/offerHelpers/awardInputCreator';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';
import { AwardInputType } from '@occc-app/offerHelpers/types';

export class EarningMechanismDuplicationService {
  private static throwNotSupportedEarningMechanismError(_earningMechanism: never): never {
    throw new Error('Not supported earning mechanism');
  }

  public copy(earningMechanism: NonNullable<OfferFragment['earningMechanism']>) {
    if (earningMechanismHelper.isVarietyPurchase(earningMechanism)) {
      return this.copyVarietyPurchase(earningMechanism);
    }

    if (earningMechanismHelper.isVarietyPurchaseOverTime(earningMechanism)) {
      return this.copyVarietyPurchaseOverTime(earningMechanism);
    }

    if (earningMechanismHelper.isVarietyDiscount(earningMechanism)) {
      return this.copyVarietyDiscount(earningMechanism);
    }

    if (earningMechanismHelper.isProductDiscount(earningMechanism)) {
      return this.copyProductDiscount(earningMechanism);
    }

    if (earningMechanismHelper.isTransactionDiscount(earningMechanism)) {
      return this.copyTransactionDiscount(earningMechanism);
    }

    if (earningMechanismHelper.isVisits(earningMechanism)) {
      return this.copyVisits(earningMechanism);
    }

    if (earningMechanismHelper.isTotalSpendOverTime(earningMechanism)) {
      return this.copyTotalSpendOverTime(earningMechanism);
    }

    if (earningMechanismHelper.isPointSpend(earningMechanism)) {
      return this.copyPointSpend(earningMechanism);
    }

    if (earningMechanismHelper.isProductPurchase(earningMechanism)) {
      return this.copyProductPurchase(earningMechanism);
    }

    if (earningMechanismHelper.isTotalSpendOnTransaction(earningMechanism)) {
      return this.copyTotalSpendOnTransaction(earningMechanism);
    }

    if (earningMechanismHelper.isTotalEligiblePurchase(earningMechanism)) {
      return this.copyTotalEligiblePurchase(earningMechanism);
    }

    if (earningMechanismHelper.isLoyaltyEvent(earningMechanism)) {
      return this.copyLoyaltyEvent(earningMechanism);
    }

    if (earningMechanismHelper.isExternalFulfillment(earningMechanism)) {
      return this.copyExternalFulfillment(earningMechanism);
    }

    if (earningMechanismHelper.isMembershipEvent(earningMechanism)) {
      return this.copyMembershipEvent(earningMechanism);
    }

    if (earningMechanismHelper.isTotalEligiblePurchaseOverTime(earningMechanism)) {
      return this.copyTotalEligiblePurchaseOverTime(earningMechanism);
    }

    if (earningMechanismHelper.isSpinWheelGame(earningMechanism)) {
      return this.copySpinWheelGame(earningMechanism);
    }

    // PromoCodes should not have "Copy" feature
    // Copying "promocode values" is not possible because promocode cant be used in different offers
    if (earningMechanismHelper.isPromocodes(earningMechanism)) {
      throw new Error('EarningMechanismPromocodes can not be copied');
    }

    // TODO: Implement EarningMechanismStoredValueDiscount
    if (earningMechanismHelper.isEarningMechanismStoredValueDiscount(earningMechanism)) {
      throw new Error('EarningMechanismStoredValueDiscount can not be copied');
    }

    // Assert that we've handled all Earning Mechanism types.
    // If a new type is added, TypeScript will flag this as an error.
    return EarningMechanismDuplicationService.throwNotSupportedEarningMechanismError(
      earningMechanism,
    );
  }

  private getAwardVouchers(award: AwardVouchersFragment) {
    const { voucherCount, voucheredOffer, voucherExpirationDays } = award;

    // TODO: replace with awardInputCreator method
    return {
      awardVouchers: {
        voucherCount,
        voucheredOfferId: voucheredOffer.id,
        voucherExpirationDays,
      },
    };
  }

  private getAwardPointsOrVouchers(
    award: AwardPointsFragment | AwardVouchersFragment | AwardExternalFragment,
  ) {
    let awardInput: AwardInputType;
    if (awardHelper.isPointsAward(award)) {
      const { points } = award;
      awardInput = awardInputCreator.createAwardPoints(points);
    } else if (awardHelper.isVouchersAward(award)) {
      awardInput = this.getAwardVouchers(award);
    } else {
      throw new Error('Not supported award');
    }

    return awardInput;
  }

  private getAwardExternalPointsOrPointsOrVouchers(
    award?: AwardPointsFragment | AwardVouchersFragment | AwardExternalPointsFragment | null,
  ) {
    if (award && awardHelper.isPointsAward(award)) {
      return awardInputCreator.createAwardPoints(award.points);
    }

    if (award && awardHelper.isVouchersAward(award)) {
      return this.getAwardVouchers(award);
    }

    if (award && awardHelper.isExternalPointsAward(award)) {
      return awardInputCreator.createAwardExternalPoints(award.maximum);
    }

    return null;
  }

  private getAwardPointsPer(award: AwardPointsPerFragment) {
    const { points, rounding } = award;
    const awardInput: {
      awardPointsPer: AwardPointsPerInput;
    } = awardInputCreator.createAwardPointsPer(points, rounding);

    return awardInput.awardPointsPer;
  }

  private copyProductPurchase(earningMechanism: EarningMechanismProductPurchaseFragment) {
    const { purchaseThreshold, eligibleProductGroups, countProductsBy } = earningMechanism;
    const award = earningMechanism.productPurchaseAward;

    const awardInput: AwardInputType = (() => {
      if (!award) {
        return null;
      }
      if (awardHelper.isExternalAward(award)) {
        return awardInputCreator.createAwardExternal(award.provider);
      }
      return this.getAwardPointsOrVouchers(award);
    })();

    return earningMechanismInputCreator.createProductPurchase(
      purchaseThreshold,
      eligibleProductGroups,
      awardInput,
      countProductsBy,
    );
  }

  private copyPointSpend(earningMechanism: EarningMechanismPointSpendFragment) {
    const { pointCost } = earningMechanism;
    const award = earningMechanism.pointSpendAward;
    const awardVouchers = this.getAwardVouchers(award);

    return earningMechanismInputCreator.createPointSpend(pointCost, awardVouchers.awardVouchers);
  }

  private copyTotalSpendOverTime(earningMechanism: EarningMechanismTotalSpendOverTimeFragment) {
    const { accumulationThreshold, ineligibleProductGroups, stayVisibleWhileInProgress } =
      earningMechanism;
    const award = earningMechanism.totalSpendOverTimeAward;
    const awardInput: AwardInputType = this.getAwardPointsOrVouchers(award);

    return earningMechanismInputCreator.createTotalSpendOverTime(
      accumulationThreshold,
      ineligibleProductGroups,
      awardInput,
      stayVisibleWhileInProgress,
    );
  }

  private copyTotalSpendOnTransaction(
    earningMechanism: EarningMechanismTotalSpendOnTransactionFragment,
  ) {
    const { roundMoneyAmount, ineligibleProductGroups, totalSpendOnTransactionAward } =
      earningMechanism;
    const awardInput: AwardPointsPerInput = this.getAwardPointsPer(totalSpendOnTransactionAward);

    return earningMechanismInputCreator.createTotalSpendOnTransaction(
      roundMoneyAmount,
      ineligibleProductGroups,
      awardInput,
    );
  }

  private copyTotalEligiblePurchase(
    earningMechanism: EarningMechanismTotalEligiblePurchaseFragment,
  ) {
    const {
      countProductsBy,
      roundCountedProducts,
      eligibleProductGroups,
      totalEligiblePurchaseAward,
    } = earningMechanism;
    const awardInput: AwardPointsPerInput = this.getAwardPointsPer(totalEligiblePurchaseAward);

    return earningMechanismInputCreator.createTotalEligiblePurchase(
      countProductsBy,
      roundCountedProducts,
      eligibleProductGroups,
      awardInput,
    );
  }

  private copyVisits(earningMechanism: EarningMechanismVisitsFragment) {
    const { visitThreshold } = earningMechanism;
    const award = earningMechanism.visitsAward;
    const awardInput: AwardInputType = this.getAwardPointsOrVouchers(award);

    return earningMechanismInputCreator.createVisits(visitThreshold, awardInput);
  }

  private copyVarietyPurchase(earningMechanism: EarningMechanismVarietyPurchaseFragment) {
    const { products } = earningMechanism;
    const award = earningMechanism.varietyPurchaseAward;
    const awardInput: AwardInputType = this.getAwardPointsOrVouchers(award);

    return earningMechanismInputCreator.createVarietyPurchase(products, awardInput);
  }

  private copyTransactionDiscount(earningMechanism: EarningMechanismTransactionDiscountFragment) {
    const { purchaseAmountThreshold, ineligibleProductGroups } = earningMechanism;
    const ineligibleProductGroupIds = ineligibleProductGroups.map((group) => group.id);
    const award = earningMechanism.transactionDiscountAward;

    let awardInput: Pick<
      EarningMechanismTransactionDiscountInput,
      'awardTransactionMoneyDiscount' | 'awardTransactionPercentDiscount'
    >;

    if (awardHelper.isTransactionMoneyDiscountAward(award)) {
      const { moneyDiscount } = award;

      awardInput = {
        awardTransactionMoneyDiscount: {
          moneyDiscount,
        },
      };
    } else if (awardHelper.isTransactionPercentDiscountAward(award)) {
      const { percentDiscount } = award;

      awardInput = {
        awardTransactionPercentDiscount: {
          percentDiscount,
        },
      };
    } else {
      throw new Error('Not supported award');
    }

    return earningMechanismInputCreator.createTransactionDiscount(
      purchaseAmountThreshold,
      ineligibleProductGroupIds,
      awardInput,
    );
  }

  private copyProductDiscount(earningMechanism: EarningMechanismProductDiscountFragment) {
    const { purchaseThresholdInt, eligibleProductGroups } = earningMechanism;
    const eligibleProductGroupIds = eligibleProductGroups.map((group) => group.id);
    const award = earningMechanism.productDiscountAward;

    let awardInput: Pick<
      EarningMechanismProductDiscountInput,
      'awardMoneyDiscount' | 'awardPriceOverride' | 'awardBuyAndGet'
    >;
    if (awardHelper.isMoneyDiscountAward(award)) {
      const { moneyAmount, awardPer } = award;
      awardInput = {
        awardMoneyDiscount: {
          moneyAmount,
          awardPer,
        },
      };
    } else if (awardHelper.isPriceOverrideAward(award)) {
      const { maximumCost } = award;
      awardInput = {
        awardPriceOverride: {
          maximumCost,
        },
      };
    } else if (awardHelper.isBuyAndGetAward(award)) {
      const { discountThreshold, percentDiscount } = award;
      awardInput = {
        awardBuyAndGet: {
          discountThreshold,
          percentDiscount,
        },
      };
    } else {
      throw new Error('Not supported award');
    }

    return earningMechanismInputCreator.createProductDiscount(
      purchaseThresholdInt,
      eligibleProductGroupIds,
      awardInput,
      earningMechanism.allowCarryOver ?? undefined,
    );
  }

  private copyVarietyDiscount(earningMechanism: EarningMechanismVarietyDiscountFragment) {
    const { purchases } = earningMechanism;
    const award = earningMechanism.varietyDiscountAward;

    const purchasesInput: EarningMechanismVarietyDiscountInput['purchases'] = purchases.map(
      ({ eligibleProductGroups, purchaseThreshold }) => {
        return {
          eligibleProductGroupIds: eligibleProductGroups.map((group) => group.id),
          purchaseThreshold,
        };
      },
    );

    let awardInput: Pick<
      EarningMechanismVarietyDiscountInput,
      'awardProductDiscount' | 'awardTransactionMoneyDiscount' | 'awardPriceOverride'
    >;

    if (awardHelper.isProductDiscountAward(award)) {
      awardInput = {
        awardProductDiscount: {
          eligibleProductGroupIds: award.eligibleProductGroups.map((group) => group.id),
          purchaseThreshold: award.purchaseThreshold,
          percentDiscount: award.percentDiscount,
        },
      };
    } else if (awardHelper.isTransactionMoneyDiscountAward(award)) {
      awardInput = {
        awardTransactionMoneyDiscount: {
          moneyDiscount: award.moneyDiscount,
        },
      };
    } else if (awardHelper.isPriceOverrideAward(award)) {
      awardInput = {
        awardPriceOverride: {
          maximumCost: award.maximumCost,
        },
      };
    } else {
      throw new Error('Not supported award');
    }

    return earningMechanismInputCreator.createVarietyDiscount(purchasesInput, awardInput);
  }

  private copyVarietyPurchaseOverTime(
    earningMechanism: EarningMechanismVarietyPurchaseOverTimeFragment,
  ) {
    const {
      products,
      varietyPurchaseOverTimeAward,
      preventDoublePunches,
      stayVisibleWhileInProgress,
      hiddenUntilProgress,
    } = earningMechanism;
    const awardInput: AwardInputType = this.getAwardPointsOrVouchers(varietyPurchaseOverTimeAward);

    const earningMechanismConfiguration =
      earningMechanismInputCreator.createVarietyPurchaseOverTime(
        products.map((item) => {
          return {
            purchaseThreshold: item.purchaseThreshold,
            countProductsBy: item.countProductsBy,
            eligibleProductGroups: item.eligibleProductGroups,
          };
        }),
        awardInput,
        preventDoublePunches || undefined,
        stayVisibleWhileInProgress,
        hiddenUntilProgress,
      );

    return earningMechanismConfiguration;
  }

  private copyLoyaltyEvent(earningMechanism: EarningMechanismLoyaltyEventFragment) {
    const award = earningMechanism.loyaltyEventAward;
    const awardInput: AwardInputType = this.getAwardExternalPointsOrPointsOrVouchers(award);

    const earningMechanismConfiguration = earningMechanismInputCreator.createLoyaltyEvent(
      earningMechanism.loyaltyEventEventType,
      awardInput,
    );

    return earningMechanismConfiguration;
  }

  private copyExternalFulfillment(earningMechanism: EarningMechanismExternalFulfillmentFragment) {
    const award = earningMechanism.externalFulfillmentAward;
    const awardInput = this.getAwardPointsOrVouchers(award!);

    const earningMechanismConfiguration =
      earningMechanismInputCreator.createExternalFulfillment(awardInput);

    return earningMechanismConfiguration;
  }

  private copyMembershipEvent(earningMechanism: EarningMechanismMembershipEventFragment) {
    const { membershipEventAward, membershipEventEventType } = earningMechanism;

    const awardInput = this.getAwardPointsOrVouchers(membershipEventAward);

    const earningMechanismConfiguration = earningMechanismInputCreator.createMembershipEvent(
      membershipEventEventType,
      awardInput,
    );

    return earningMechanismConfiguration;
  }

  private copyTotalEligiblePurchaseOverTime(
    earningMechanism: EarningMechanismTotalEligiblePurchaseOverTimeFragment,
  ) {
    const {
      accumulationThreshold,
      countProductsBy,
      eligibleProductGroups,
      totalEligiblePurchaseOverTimeAward,
      hiddenUntilProgress,
    } = earningMechanism;

    const awardInput = this.getAwardPointsOrVouchers(totalEligiblePurchaseOverTimeAward);
    const eligibleProductGroupIds = eligibleProductGroups.map((group) => group.id);

    const earningMechanismConfiguration =
      earningMechanismInputCreator.createTotalEligiblePurchaseOverTime(
        accumulationThreshold,
        countProductsBy,
        eligibleProductGroupIds,
        awardInput,
        hiddenUntilProgress,
      );

    return earningMechanismConfiguration;
  }

  private copySpinWheelGame(
    earningMechanism: EarningMechanismSpinWheelGameFragment,
  ): EarningMechanismConfigurationInput {
    return {
      spinWheelGame: {
        purchasableOfferId: earningMechanism.purchasableOffer?.id ?? null,
        awardRandomPrize: {
          awards: earningMechanism.awards.map((award) => ({
            awardType: award.awardType,
            awardPoints:
              award.awardType === Random_Award_Type.Points &&
              award.award?.__typename === 'AwardPoints'
                ? { points: award.award.points }
                : null,
            awardVouchers:
              award.awardType === Random_Award_Type.Vouchers &&
              award.award?.__typename === 'AwardVouchers'
                ? {
                    // TODO confirm only 1 is possible
                    voucherCount: 1,
                    voucheredOfferId: award.award.voucheredOffer.id,
                    voucherExpirationDays: award.award.voucherExpirationDays,
                  }
                : null,
            limit: award.limit,
            sequenceNumber: award.sequenceNumber,
          })),
        },
      },
    };
  }

  // PromoCodes should not have "Copy" feature
  // Copying "promocode values" is not possible because promocode cant be used in different offers
  // TODO uncomment when EM updated
  // private copyPromoCodes(
  //   earningMechanism: EarningMechanismPromocodesFragment,
  // ): EarningMechanismConfigurationInput {
  //   return { promocodes: { promocodes: null } };
  // }
}

export const earningMechanismDuplicationService = new EarningMechanismDuplicationService();
